import { noop } from "lodash-es";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useGQLKeyInvalidator } from "../hooks/useGQLKeyInvalidator";

interface IContextProps {
    subscribe(): void;
    unsubscribe(): void;
    refreshMillis: number;
}

const DEFAULT: IContextProps = {
    subscribe: noop,
    unsubscribe: noop,
    refreshMillis: Infinity,
};

const EXCHANGE_RATE_REFRESH_MILLIS = 60 * 1000;

export const ExchangeRateSubscriptionContext = React.createContext<IContextProps>(DEFAULT);

export const ExchangeRateSubscriptionContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [numSubscribers, setNumberSubscribers] = useState<number>(0);
    const incrementNumSubscribers = useCallback((): void => setNumberSubscribers((prevState) => prevState + 1), []);
    const decrementNumSubscribers = useCallback(
        (): void => setNumberSubscribers((prevState) => Math.max(prevState - 1, 0)),
        [],
    );

    const invalidateRates = useGQLKeyInvalidator("GetRatesResponse");

    const shouldRefetch = numSubscribers > 0;
    useEffect(() => {
        if (!shouldRefetch) {
            return;
        }
        const intervalId = setInterval((): void => {
            invalidateRates();
        }, EXCHANGE_RATE_REFRESH_MILLIS);

        return () => {
            clearInterval(intervalId);
        };
    }, [invalidateRates, shouldRefetch]);

    const value = useMemo(
        () => ({
            subscribe: incrementNumSubscribers,
            unsubscribe: decrementNumSubscribers,
            refreshMillis: EXCHANGE_RATE_REFRESH_MILLIS,
        }),
        [incrementNumSubscribers, decrementNumSubscribers],
    );

    return (
        <ExchangeRateSubscriptionContext.Provider value={value}>{children}</ExchangeRateSubscriptionContext.Provider>
    );
};
